/**
 * **S2 — the attack's derived facts.** The second stage of `attack.ts`'s `resolveSingleTarget`
 * pipeline, and the one derivation `fireAttackDeclared` also reads: which weapon and which of
 * its firing profiles this attack actually fires with, plus the static attack-family facts the
 * effects engine matches on (`ranged`/`tech`/`weaponType`/`mount`/`firstAttackThisTurn`), the
 * composite granted-action id every attack-family fire and fold matches on (`actionId`), and the
 * attack verb the modifier folds scope against.
 *
 * Pure over its arguments: it reads `state` and returns a record, mutating nothing, drawing no
 * dice, and emitting no step. That purity is what lets the two callers share it — the facts do
 * not depend on where in the resolution they are computed, so `fireAttackDeclared` (which runs
 * BEFORE `resolveSingleTarget`) and `resolveSingleTarget` itself get the same answers from one
 * derivation instead of two that are merely hoped to agree.
 */

import type { AttackFx, AttackVerb, Combatant, EncounterState, WeaponProfile } from "@lancer-vtt/domain";
import { attacksDeclaredThisTurn, threatValue } from "@lancer-vtt/domain";
import { attackFx } from "../attackFx";
import type { RequestAttack } from "@lancer-vtt/shared";
import { actionIdForAttackLike, frozenTechActivation, reactionAttackName } from "../economy";
import { attackHandlerId, firingProfile, isMeleeActionAttack, selectWeapon, techAttackProfile } from "./weapons";
import { attackTargetDistance, isRangedWeaponAttack } from "./weaponMode";
import type { EffectsConfig } from "./types";

/** What {@link deriveAttackFacts} settles for the rest of the resolution. */
export type AttackFacts = {
  /**
   * The **base** weapon — the multi-profile parent, selected with no `profileId`. It is what
   * keys the overlay entry: a multi-profile weapon's triggered handlers (the Apocalypse Rail's
   * on-hit die-reset + riders) are gathered against the base id, NOT the selected alternate
   * profile's id, which differs.
   */
  baseWeapon: WeaponProfile;
  /** {@link baseWeapon}'s id — what the overlay handler gather keys off. */
  baseWeaponId: string;
  /**
   * A **melee action attack** (Ram, …): the non-tech, vs-Evasion analogue of a tech attack
   * (INVADE). Like a tech attack it emits NO weapon damage — its consequence rides the on-hit
   * overlay handlers (gathered off the action id) — but it rolls vs Evasion, not E-DEF.
   */
  isActionAttack: boolean;
  /**
   * The **active firing profile** — the frozen profile whose damage/tags drive the roll.
   * D6 selection, delegated wholesale to {@link firingProfile} (tracker D5.1): a recorded
   * `intent.profile` (a player's choice) wins; absent, the overlay's `select` effect picks a
   * profile **by state** (the Apocalypse Die value → Charge-Rail tier); else the weapon's
   * default. Neither path emits a step — they change which frozen profile the to-hit/damage
   * steps record.
   */
  weapon: WeaponProfile;
  /**
   * Engaged's ranged-only gate (status-catalog-completion P1.1, RAW p.62 "ranged attacks"):
   * the SAME `isRangedAttack(weapon.range)` + `!intent.tech` shape the S2.5 cover fold ({@link
   * import("./attackModifiers").foldAttackCover}) uses (a tech attack targets E-DEFENSE by a
   * different mechanism, never "a ranged
   * attack" in this RAW sense). Threaded onto the attack-family event so ENGAGED's
   * `match: { family: "attack", ranged: true }` modifier reads it — derived once here rather
   * than duplicated at the cover-fold site. (The cover fold still applies
   * `isRangedAttack(weapon.range)` itself, because its own gate also admits a `tg_thrown` melee
   * weapon — a wider condition than this fact, not the same one restated.)
   *
   * Read off the SELECTED profile's `range`, not {@link baseWeapon}'s — an alternate firing
   * profile may carry its own range, and the profile that fires is the one whose range counts.
   * An either-mode weapon (the Ram Cannon) answers with its resolved mode instead of its range
   * shape (`./weaponMode`), so an adjacent swing is melee here and everywhere this fact flows.
   */
  rangedAttack: boolean;
  /**
   * `ll0-dsl-primitives-6` P1: the native attack-family `match.tech` fact's one source for this
   * resolution — the SAME `intent.tech` flag {@link rangedAttack}'s own `!intent.tech` posture
   * reads, threaded onto every predicate/fire context this resolution builds — the pre-roll
   * `pctx` and the roll replacements' facts (in {@link import("./attackModifiers").foldAttackModifiers}
   * and {@link import("./attackModifiers").damageRollReplacements}), and
   * the on_hit/on_crit and on_miss `fireCtx`s (in
   * {@link import("./attackTriggerFires").fireHitTriggers} /
   * {@link import("./attackTriggerFires").fireMissTriggers}) — exactly
   * like `rangedAttack`/`weaponType`/`mount`/`firstAttackThisTurn` already are.
   */
  techAttack: boolean;
  /**
   * Native attack-family `match.weaponType` fact's one source (`ll0-dsl-primitives-2` Phase B,
   * crack_shot's "ranged attack with a Rifle"). Read off {@link baseWeapon} (the multi-profile
   * PARENT), not the profile-selected {@link weapon} — a weapon's class doesn't change between
   * firing modes, and (mirroring {@link mount}) an alternate firing profile never carries its
   * own frozen `type` in the first place (`freezeWeaponProfile` only stamps it onto the
   * top-level profile). NOT conditioned on `!intent.tech` the way {@link rangedAttack} is — a
   * tech attack's base is the weaponless `techAttackProfile` and a melee action attack's is the
   * synthetic `MELEE_ACTION_ATTACKS` profile, neither of which carries a weapon class, so this
   * reads `undefined` for both without a second bespoke gate; a content author combines
   * `weaponType`/`ranged` (both on the same `match`) when both facts matter.
   */
  weaponType: string | undefined;
  /**
   * Native attack-family `match.mount` fact's one source (`ll0-dsl-primitives-2` Phase C,
   * duelist's "Main Melee weapon", gunslinger's "Auxiliary ranged weapon"). Read off
   * {@link baseWeapon} for the SAME reason {@link weaponType} is: mount size doesn't change
   * between firing modes, and `freezeWeaponProfile` only stamps `mount` onto the top-level
   * profile, never `altProfiles`. `mount` was ALREADY frozen (Barrage/Superheavy read it) —
   * this is only the event-fact thread, no new freeze site. Combine
   * `mount`/`weaponType`/`ranged` (all on the same `match`) when a clause needs multiple facts.
   */
  mount: string | undefined;
  /**
   * Native attack-family `match.firstAttackThisTurn` fact's one source (`ll0-dsl-primitives-2`
   * Phase D, `decisions/0085`): the first attack since the attacker's own turn started, whoever's
   * turn it is now, counting every attack. Infiltrator 2 reads it. RAW's "the first <kind of>
   * attack … on your turn" (Duelist, Gunslinger, Exemplar, Nuclear Cavalier) is authored instead as
   * `on_turn(self)` plus a `per: "own_turn"` cap, since this fact admits an off-turn Overwatch and
   * counts attacks the clause doesn't match.
   *
   * Reads the ATTACKER's per-turn ordinal BEFORE this attack's own increment lands. State
   * doesn't mutate mid-resolution, so `attacker.current.resources` still reflects the
   * pre-this-attack count at BOTH call sites — `fireAttackDeclared`, which computes the +1 step
   * into `declared.steps`, and `resolveSingleTarget`, which runs after it. 0 declared so far
   * this turn ⇒ this IS the first attack. The counter itself resets at the fired `turn_start`
   * (`resolveAttackOrdinalReset`) and increments at the fired `on_attack_declared`
   * (`fireAttackDeclared`) — both Phase-A fired points, so this is replay-safe by construction.
   */
  firstAttackThisTurn: boolean;
  /**
   * `ll0-dsl-primitives-8` D5: the in-flight attack's own attack verb (`ATTACK_VERBS`) — the
   * "ram"/"grapple"/"skirmish"/"fight"/"quick_tech"/"full_tech"/"improvised_attack" id
   * `actionIdForIntent` derives for the once-per-turn ledger (`decisions/0052`). Threaded into
   * `scopeAttackModifiers` at BOTH attacker-side folds — the pre-roll `atkMods` inside {@link
   * import("./attackModifiers").foldAttackModifiers} (S3) and the post-roll `dmgMods` re-fold
   * still inline in `resolveSingleTarget` — so an `attackScope`-scoped modifier
   * (t_juggernaut:1's "your next Ram gets +1 Accuracy") strips correctly against whatever this
   * ATTACK actually is, never guessed — and read as `declaredVerb` by `fireAttackDeclared`'s own
   * fire context. Derived directly (not via `actionIdForIntent`, which also handles
   * Barrage/Overcharge/activate shapes this resolver never sees) — mirroring
   * {@link rangedAttack}/{@link techAttack}'s own "compute the one fact this resolution needs"
   * posture.
   *
   * Deriving it HERE, once, is what closes the duplication `fireAttackDeclared` used to concede
   * in prose: the two sites called the same `actionIdForAttackLike` with the same inputs and
   * were merely *believed* to agree. Now there is one call.
   */
  attackVerb: AttackVerb;
  /**
   * The **composite granted-action id** this attack fires as — `attackHandlerId(intent,
   * baseWeaponId)`: `"ms_markerlight:0"` for a system-granted tech action, and the base weapon's
   * OWN id (never `undefined`) for an ordinary weapon attack. The one fact behind
   * {@link import("../effects/family").AttackMatch.actionId}, and the id the overlay handler
   * gathers key off at every attack-family fire site.
   *
   * **Derived HERE, once, for the same reason `attackVerb` is** (`system-granted-tech-attacks`
   * D8 + tracker D5.2): before `npc-features-fielded-roster` Phase 8.1 the modifier folds had no
   * weapon identity at all, and `fireAttackDeclared` re-derived this from `baseWeapon.id` beside
   * its own `deriveAttackFacts` call. Both now read this field, so a `modifier` clause's
   * `match.actionId` and a `triggered` clause's `match.actionId` cannot disagree about what the
   * in-flight attack is.
   *
   * `attackTriggerFires.ts`'s two RESOLVED fires still call `attackHandlerId` themselves off the
   * `baseWeaponId` they are handed — the same pure helper on the same inputs, left alone rather
   * than widening two more args objects.
   */
  actionId: string;
  /**
   * How this attack looks on the map (`attack-animations` Phase 1): {@link attackFx} over the
   * selected firing profile, the base weapon's class and id, and this resolution's resolved mode,
   * so a Ram Cannon swung at Threat is a strike and fired beyond it a heavy round. Stamped on every
   * `to_hit` the resolution records ({@link import("./toHit").drawToHit}).
   */
  fx: AttackFx;
  /**
   * The named reaction this attack spends (`reactionAttackName`): `"covering_fire"`,
   * `"overwatch"`, or `undefined` when it isn't a reaction. The attack-family `reaction` event
   * fact, set at the declared fire and both resolved fires.
   */
  reaction: string | undefined;
};

/** Args for {@link deriveAttackFacts} (max-params-3 discipline, `CODE_STYLE.md`). */
export type DeriveAttackFactsArgs = {
  state: EncounterState;
  attacker: Combatant;
  intent: RequestAttack;
  effects: EffectsConfig | undefined;
};

export function deriveAttackFacts(args: DeriveAttackFactsArgs): AttackFacts {
  const { state, attacker, intent, effects } = args;

  // ── the weapon and its active firing profile ──
  // The *base* weapon (the multi-profile parent) keys the overlay entry; `weapon` is the
  // selected firing profile, whose frozen tags feed the attacker-side weapon-tag modifiers
  // (Accurate {VAL}, …) and the on_crit gather back in `resolveSingleTarget`. The selection
  // itself is NOT restated here (tracker D5.1): `firingProfile` in `./weapons` exists to be
  // the single derivation — "kept in one place so the shared-pool roll and the per-target
  // spine always agree on the weapon" — and this is the site it most needs to cover.
  // A tech attack fires no weapon, so its base is the same weaponless `techAttackProfile`
  // `firingProfile` returns — never the first-weapon fallback `selectWeapon` would give an
  // action id it cannot find (`tech-attack-inherits-weapon-tags`).
  const baseWeapon = intent.tech ? techAttackProfile(intent, attacker) : selectWeapon(attacker, intent.weapon, undefined);
  const weapon = firingProfile({ state, attacker, intent, effects });
  const rangedAttack = !intent.tech && isRangedWeaponAttack({ state, attacker, intent, weapon });

  return {
    baseWeapon,
    baseWeaponId: baseWeapon.id,
    isActionAttack: isMeleeActionAttack(intent.weapon),
    weapon,
    rangedAttack,
    techAttack: intent.tech === true,
    weaponType: baseWeapon.type,
    mount: baseWeapon.mount,
    firstAttackThisTurn: attacksDeclaredThisTurn(attacker.current.resources) === 0,
    actionId: attackHandlerId(intent, baseWeapon.id),
    reaction: reactionAttackName(state, intent),
    attackVerb: actionIdForAttackLike({
      weapon: intent.weapon,
      tech: intent.tech,
      attackerKind: attacker.kind,
      techActivation: frozenTechActivation(state, attacker.id, intent.weapon),
    }),
    fx: attackFx({
      profile: weapon,
      weapon: baseWeapon,
      tech: intent.tech === true,
      ranged: rangedAttack,
      thrown: !intent.tech && !rangedAttack && isThrownBeyondThreat({ state, attacker, intent, weapon }),
    }),
  };
}

/**
 * A class-Melee weapon with `tg_thrown` aimed beyond its Threat: the attack is the throw, which the
 * map draws as a spinning blade rather than a swing.
 */
function isThrownBeyondThreat(args: { state: EncounterState; attacker: Combatant; intent: RequestAttack; weapon: WeaponProfile }): boolean {
  const { weapon } = args;
  if (!weapon.tags?.some((t) => t.id === "tg_thrown")) return false;
  const distance = attackTargetDistance(args);
  return distance !== null && distance > threatValue(weapon.range ?? []);
}
